"use client";

import { useEffect, useState, type ReactNode } from "react";
import {
  Plus,
  Pencil,
  ArrowUp,
  ArrowDown,
  Eye,
  EyeOff,
  Loader2,
  X,
} from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  createReviewQuestion,
  updateReviewQuestion,
  setReviewQuestionActive,
  reorderReviewQuestion,
  cloneGlobalQuestionsToShow,
  listReviewQuestions,
} from "@/lib/reviews/admin-actions";

const LANGS = ["en", "da", "no", "sv"] as const;

const TYPES = [
  { value: "rating", label: "Star rating (1–5)" },
  { value: "nps", label: "NPS (0–10)" },
  { value: "single_choice", label: "Single choice" },
  { value: "multi_choice", label: "Multiple choice" },
  { value: "bool", label: "Yes / No" },
  { value: "text", label: "Free text" },
  { value: "email", label: "Email" },
  { value: "name", label: "Name" },
] as const;

type QuestionType = (typeof TYPES)[number]["value"];

type QuestionCategory = "survey" | "feedback";

const CATEGORIES: { value: QuestionCategory; label: string }[] = [
  { value: "feedback", label: "Feedback" },
  { value: "survey", label: "Survey" },
];

export interface ReviewQuestionRow {
  id: number;
  venue_id: number | null;
  question_key: string;
  type: QuestionType;
  category: QuestionCategory;
  required: boolean;
  allow_other: boolean;
  order_index: number;
  is_active: boolean;
  labels: Record<string, string>;
  descriptions?: Record<string, string> | null;
  options: { value: string; labels: Record<string, string> }[] | null;
}

interface FormState {
  type: QuestionType;
  category: QuestionCategory;
  required: boolean;
  allowOther: boolean;
  labels: Record<string, string>;
  /** Optional example / additional information, per language. */
  descriptions: Record<string, string>;
  options: { value: string; labels: Record<string, string> }[];
}

// Seed an empty label map keyed by the venue's languages, so we never persist
// blank labels for languages the venue doesn't use.
const seedLabels = (langs: string[]): Record<string, string> =>
  Object.fromEntries(langs.map((l) => [l, ""]));

const emptyForm = (langs: string[]): FormState => ({
  type: "rating",
  category: "feedback",
  required: false,
  allowOther: false,
  labels: seedLabels(langs),
  descriptions: seedLabels(langs),
  options: [],
});

const isChoice = (type: QuestionType) =>
  type === "single_choice" || type === "multi_choice";

const typeLabel = (type: string) =>
  TYPES.find((t) => t.value === type)?.label ?? type;

export function ReviewQuestionsClient({
  venueId,
  showId,
  questions,
  languages,
  hasGlobalDefaults,
  headerActions,
}: {
  venueId: string;
  /** Show these questions belong to — all reads/writes are scoped to it. */
  showId: number;
  questions: ReviewQuestionRow[];
  /**
   * Languages configured for the venue (codes, e.g. ["en", "sv"]). The label
   * editor only shows inputs for these. Falls back to all supported languages
   * when not provided.
   */
  languages?: string[];
  hasGlobalDefaults: boolean;
  headerActions?: ReactNode;
}) {
  const venueIdNum = Number(venueId);

  // Only offer label inputs for the venue's languages. Guard against an empty
  // list so the dialog never renders with zero language fields.
  const langs =
    languages && languages.length > 0 ? languages : ([...LANGS] as string[]);

  const [rows, setRows] = useState<ReviewQuestionRow[]>(questions);
  // Keep in sync if the server re-renders with new props.
  useEffect(() => setRows(questions), [questions]);

  const refreshRows = async () => {
    const fresh = await listReviewQuestions(showId);
    setRows(fresh as ReviewQuestionRow[]);
  };

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<ReviewQuestionRow | null>(null);
  const [form, setForm] = useState<FormState>(() => emptyForm(langs));
  const [saving, setSaving] = useState(false);
  const [busyId, setBusyId] = useState<number | null>(null);

  const openAdd = () => {
    setEditing(null);
    setForm(emptyForm(langs));
    setDialogOpen(true);
  };

  const openEdit = (q: ReviewQuestionRow) => {
    setEditing(q);
    setForm({
      type: q.type,
      category: q.category ?? "feedback",
      required: q.required,
      allowOther: q.allow_other,
      labels: { ...seedLabels(langs), ...q.labels },
      descriptions: { ...seedLabels(langs), ...(q.descriptions ?? {}) },
      options: (q.options ?? []).map((o) => ({
        value: o.value,
        labels: { ...seedLabels(langs), ...o.labels },
      })),
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.labels.en?.trim()) {
      toast.error("An English label is required.");
      return;
    }
    setSaving(true);
    const input = {
      venueId: venueIdNum,
      showId,
      type: form.type,
      category: form.category,
      required: form.required,
      allowOther: isChoice(form.type) ? form.allowOther : false,
      labels: form.labels,
      // Blank ones are dropped, so the form shows nothing for that language.
      descriptions: Object.fromEntries(
        Object.entries(form.descriptions)
          .map(([lang, text]) => [lang, text.trim()])
          .filter(([, text]) => text),
      ),
      options: isChoice(form.type)
        ? form.options
            .filter((o) => o.labels.en?.trim())
            .map((o) => ({
              value: o.value?.trim() || slug(o.labels.en),
              labels: o.labels,
            }))
        : null,
    };
    const result = editing
      ? await updateReviewQuestion(editing.id, input)
      : await createReviewQuestion(input);
    setSaving(false);

    if (result.success) {
      toast.success(editing ? "Question updated" : "Question added");
      setDialogOpen(false);
      await refreshRows();
    } else {
      toast.error(result.error ?? "Something went wrong");
    }
  };

  const handleReorder = async (q: ReviewQuestionRow, dir: "up" | "down") => {
    setBusyId(q.id);
    await reorderReviewQuestion(q.id, venueIdNum, showId, dir);
    setBusyId(null);
    await refreshRows();
  };

  const handleToggleActive = async (q: ReviewQuestionRow) => {
    setBusyId(q.id);
    const result = await setReviewQuestionActive(q.id, venueIdNum, !q.is_active);
    setBusyId(null);
    if (result.success) await refreshRows();
    else toast.error(result.error ?? "Could not update");
  };

  const handleClone = async () => {
    setSaving(true);
    const result = await cloneGlobalQuestionsToShow(venueIdNum, showId);
    setSaving(false);
    if (result.success) {
      toast.success("Default questions copied to this show");
      await refreshRows();
    } else {
      toast.error(result.error ?? "Could not copy questions");
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h3 className="text-lg font-semibold">Review questions</h3>
          <p className="text-sm text-muted-foreground">
            Questions shown on this show&apos;s QR feedback form. Drag order with
            the arrows; archive instead of deleting to keep past answers intact.
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {headerActions}
          <Button
            type="button"
            onClick={openAdd}
            className="bg-purple-600 hover:bg-purple-700"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add question
          </Button>
        </div>
      </div>

      {rows.length === 0 ? (
        <div className="text-center border rounded-lg py-10 px-6">
          <p className="text-muted-foreground mb-4">
            {hasGlobalDefaults
              ? "This show uses the global default questions. Copy them here to customize for this show, or add your own."
              : "No questions yet. Add the first question for this show."}
          </p>
          {hasGlobalDefaults && (
            <Button
              type="button"
              variant="outline"
              onClick={handleClone}
              disabled={saving}
            >
              {saving ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : null}
              Customize default questions for this show
            </Button>
          )}
        </div>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-24 text-center">Order</TableHead>
              <TableHead>Question (EN)</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Category</TableHead>
              <TableHead className="text-center">Required</TableHead>
              <TableHead className="text-center">Status</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((q, idx) => (
              <TableRow key={q.id} className={q.is_active ? "" : "opacity-50"}>
                <TableCell>
                  <div className="flex items-center justify-center gap-1">
                    <button
                      type="button"
                      disabled={idx === 0 || busyId === q.id}
                      onClick={() => handleReorder(q, "up")}
                      className="p-1 rounded hover:bg-muted disabled:opacity-30"
                    >
                      <ArrowUp className="w-4 h-4" />
                    </button>
                    <button
                      type="button"
                      disabled={idx === rows.length - 1 || busyId === q.id}
                      onClick={() => handleReorder(q, "down")}
                      className="p-1 rounded hover:bg-muted disabled:opacity-30"
                    >
                      <ArrowDown className="w-4 h-4" />
                    </button>
                  </div>
                </TableCell>
                <TableCell className="font-medium">
                  {q.labels.en || (
                    <span className="text-muted-foreground italic">
                      (no English label)
                    </span>
                  )}
                </TableCell>
                <TableCell className="text-muted-foreground">
                  {typeLabel(q.type)}
                </TableCell>
                <TableCell className="capitalize text-muted-foreground">
                  {q.category ?? "feedback"}
                </TableCell>
                <TableCell className="text-center">
                  {q.required ? "Yes" : "—"}
                </TableCell>
                <TableCell className="text-center">
                  <span
                    className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                      q.is_active
                        ? "bg-green-100 text-green-800"
                        : "bg-gray-100 text-gray-600"
                    }`}
                  >
                    {q.is_active ? "Active" : "Archived"}
                  </span>
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex items-center justify-end gap-1">
                    <button
                      type="button"
                      onClick={() => openEdit(q)}
                      className="p-1.5 rounded hover:bg-muted"
                      title="Edit"
                    >
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button
                      type="button"
                      onClick={() => handleToggleActive(q)}
                      disabled={busyId === q.id}
                      className="p-1.5 rounded hover:bg-muted"
                      title={q.is_active ? "Archive" : "Restore"}
                    >
                      {q.is_active ? (
                        <EyeOff className="w-4 h-4" />
                      ) : (
                        <Eye className="w-4 h-4" />
                      )}
                    </button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <QuestionDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        editing={editing}
        form={form}
        setForm={setForm}
        saving={saving}
        onSave={handleSave}
        languages={langs}
      />
    </div>
  );
}

function slug(input: string): string {
  return (
    input
      .toLowerCase()
      .normalize("NFKD")
      .replace(/[^\w\s-]/g, "")
      .trim()
      .replace(/\s+/g, "_")
      .slice(0, 40) || "option"
  );
}

function QuestionDialog({
  open,
  onOpenChange,
  editing,
  form,
  setForm,
  saving,
  onSave,
  languages,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  editing: ReviewQuestionRow | null;
  form: FormState;
  setForm: React.Dispatch<React.SetStateAction<FormState>>;
  saving: boolean;
  onSave: () => void;
  /** Venue languages to render label inputs for. */
  languages: string[];
}) {
  const choice = isChoice(form.type);

  const setLabel = (lang: string, value: string) =>
    setForm((f) => ({ ...f, labels: { ...f.labels, [lang]: value } }));

  const setDescription = (lang: string, value: string) =>
    setForm((f) => ({
      ...f,
      descriptions: { ...f.descriptions, [lang]: value },
    }));

  const addOption = () =>
    setForm((f) => ({
      ...f,
      options: [
        ...f.options,
        {
          value: "",
          labels: Object.fromEntries(languages.map((l) => [l, ""])),
        },
      ],
    }));

  const removeOption = (i: number) =>
    setForm((f) => ({ ...f, options: f.options.filter((_, idx) => idx !== i) }));

  const setOptionLabel = (i: number, lang: string, value: string) =>
    setForm((f) => ({
      ...f,
      options: f.options.map((o, idx) =>
        idx === i ? { ...o, labels: { ...o.labels, [lang]: value } } : o,
      ),
    }));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[640px] max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {editing ? "Edit question" : "Add question"}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-5 py-2">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Type</Label>
              <Select
                value={form.type}
                onValueChange={(v) =>
                  setForm((f) => ({ ...f, type: v as QuestionType }))
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {TYPES.map((t) => (
                    <SelectItem key={t.value} value={t.value}>
                      {t.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Category</Label>
              <Select
                value={form.category}
                onValueChange={(v) =>
                  setForm((f) => ({ ...f, category: v as QuestionCategory }))
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map((c) => (
                    <SelectItem key={c.value} value={c.value}>
                      {c.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                <span className="font-medium">Survey</span>: shown via the link in
                the review email. <span className="font-medium">Feedback</span>:
                shown on the QR code survey.
              </p>
            </div>

            <div className="flex flex-col justify-end gap-2 pb-1">
              <label className="flex items-center gap-2 text-sm">
                <Checkbox
                  checked={form.required}
                  onCheckedChange={(c) =>
                    setForm((f) => ({ ...f, required: c === true }))
                  }
                />
                Required
              </label>
              {choice && (
                <label className="flex items-center gap-2 text-sm">
                  <Checkbox
                    checked={form.allowOther}
                    onCheckedChange={(c) =>
                      setForm((f) => ({ ...f, allowOther: c === true }))
                    }
                  />
                  Allow a free-text “other” answer
                </label>
              )}
            </div>
          </div>

          <div className="space-y-3">
            <Label>Question label</Label>
            {languages.map((lang) => (
              <div key={lang} className="flex items-center gap-3">
                <span className="w-8 text-xs font-semibold uppercase text-muted-foreground">
                  {lang}
                </span>
                <Input
                  value={form.labels[lang] ?? ""}
                  onChange={(e) => setLabel(lang, e.target.value)}
                  placeholder={
                    lang === "en"
                      ? "Required — English label"
                      : `Label (${lang})`
                  }
                />
              </div>
            ))}
          </div>

          <div className="space-y-3">
            <div>
              <Label>Example / additional information</Label>
              <p className="text-xs text-muted-foreground">
                Optional. Shown in italics under the question, e.g. an example
                answer or some context. Leave a language blank to show nothing.
              </p>
            </div>
            {languages.map((lang) => (
              <div key={lang} className="flex items-start gap-3">
                <span className="w-8 pt-2 text-xs font-semibold uppercase text-muted-foreground">
                  {lang}
                </span>
                <Textarea
                  rows={2}
                  maxLength={1000}
                  value={form.descriptions[lang] ?? ""}
                  onChange={(e) => setDescription(lang, e.target.value)}
                  placeholder={`Example (${lang})`}
                />
              </div>
            ))}
          </div>

          {choice && (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label>Options</Label>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={addOption}
                >
                  <Plus className="w-3.5 h-3.5 mr-1" />
                  Add option
                </Button>
              </div>
              {form.options.length === 0 && (
                <p className="text-xs text-muted-foreground">
                  No options yet — add at least one.
                </p>
              )}
              {form.options.map((opt, i) => (
                <div key={i} className="border rounded-md p-3 space-y-2 relative">
                  <button
                    type="button"
                    onClick={() => removeOption(i)}
                    className="absolute top-2 right-2 p-1 rounded hover:bg-muted text-muted-foreground"
                    title="Remove option"
                  >
                    <X className="w-4 h-4" />
                  </button>
                  {languages.map((lang) => (
                    <div key={lang} className="flex items-center gap-3">
                      <span className="w-8 text-xs font-semibold uppercase text-muted-foreground">
                        {lang}
                      </span>
                      <Input
                        value={opt.labels[lang] ?? ""}
                        onChange={(e) => setOptionLabel(i, lang, e.target.value)}
                        placeholder={
                          lang === "en" ? "Option (English)" : `Option (${lang})`
                        }
                      />
                    </div>
                  ))}
                </div>
              ))}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={saving}
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={onSave}
            disabled={saving}
            className="bg-purple-600 hover:bg-purple-700"
          >
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
            {editing ? "Save changes" : "Add question"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
